// ═══════════════════════════════════════════════════════════════
// COMPONENTE: Tabla de Historial de Salarios
// Solo lectura — muestra cambios salariales con fecha, variación y motivo
// ═══════════════════════════════════════════════════════════════

import React from 'react';
import { TrendingUp, TrendingDown, Minus, History } from 'lucide-react';
import { useSalaryHistory } from '../../../hooks/useSalaryHistory';

const MOTIVO_LABELS = {
  contrato_inicial:         'Contrato inicial',
  actualizacion_contractual:'Actualización contractual',
  backfill_inicial:         'Registro inicial',
  ajuste_salarial:          'Ajuste salarial',
  cambio_contractual:       'Cambio contractual',
};

function formatMoney(value) {
  if (value == null) return '—';
  return parseFloat(value).toLocaleString('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  // Tomar solo la parte YYYY-MM-DD para evitar problemas de timezone
  const datePart = String(dateStr).slice(0, 10);
  return new Date(datePart + 'T12:00:00').toLocaleDateString('es-DO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

function Variacion({ anterior, nuevo }) {
  if (anterior == null || anterior === '') {
    return <span className="text-gray-400 text-xs">—</span>;
  }
  const prev = parseFloat(anterior);
  const curr = parseFloat(nuevo);
  const diff = curr - prev;
  const pct = prev > 0 ? ((diff / prev) * 100).toFixed(1) : null;

  if (diff > 0) {
    return (
      <span className="inline-flex items-center gap-1 text-green-700 font-medium text-xs">
        <TrendingUp size={13} />
        +{pct}%
      </span>
    );
  }
  if (diff < 0) {
    return (
      <span className="inline-flex items-center gap-1 text-red-600 font-medium text-xs">
        <TrendingDown size={13} />
        {pct}%
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-gray-400 text-xs">
      <Minus size={13} />
      0%
    </span>
  );
}

export default function SalaryHistoryTable({ employeeId }) {
  const { history, loading, error } = useSalaryHistory(employeeId);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-400 text-sm">
        <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin mr-2" />
        Cargando historial...
      </div>
    );
  }

  if (error) {
    return (
      <p className="text-sm text-red-500 py-4">{error}</p>
    );
  }

  if (history.length === 0) {
    return (
      <p className="text-sm text-gray-400 py-4 text-center">
        Sin registros de cambios salariales.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100">
            <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Fecha efectiva
            </th>
            <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Salario anterior
            </th>
            <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Salario nuevo
            </th>
            <th className="text-center py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Variación
            </th>
            <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Motivo
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {history.map((row) => (
            <tr key={row.id} className="hover:bg-gray-50 transition-colors">
              <td className="py-2.5 px-3 text-gray-700 whitespace-nowrap">
                {formatDate(row.fecha_efectiva)}
              </td>
              <td className="py-2.5 px-3 text-right text-gray-500 whitespace-nowrap">
                {row.salario_anterior != null
                  ? `RD$ ${formatMoney(row.salario_anterior)}`
                  : '—'}
              </td>
              <td className="py-2.5 px-3 text-right font-semibold text-gray-900 whitespace-nowrap">
                RD$ {formatMoney(row.salario_nuevo)}
              </td>
              <td className="py-2.5 px-3 text-center">
                <Variacion anterior={row.salario_anterior} nuevo={row.salario_nuevo} />
              </td>
              <td className="py-2.5 px-3 text-gray-600 capitalize">
                {MOTIVO_LABELS[row.motivo] || row.motivo || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
